import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getMerchantInfo,
  getSettingsActiveFeatures,
  getSyncState,
} from "../../services/api/retail/merchantInfoApi";
import { setPageLoading } from "../../store/slices/loadingSlice";

import { useRouter } from "../../utils/hooks/useRouter";
import { useMerchantActive } from "../../hooks/retail/merchant/useMerchantActive";
import { getShopPackageSetting } from "../../services/retail/setting/retailSettingService";
import {
  ShopInformation,
  setIsOpenNotiEndSubscription,
  setIsShopInactive,
  setIsShowAlertEndSubscription,
  setShopInfo,
  setIsShopUpgradePackageSucce,
} from "../../store/slices/shopSlice";
import { useAppSelector } from "../../store/hooks";
import _, { isEmpty } from "lodash";
import { SyncStatusType } from "../constant";
import { setSyncData, setSyncStatus } from "../../store/slices/branchSlice";
import { setOnboardingSettings } from "../../store/slices/onboardingFeatureSlice";
import { BOOKING_HOTEL_PLATFORM, BOOKING_PLATFORM, FNB_PLATFORM } from "../../utils/constant";
import { getPlatform } from "../../utils/helper";
import { TourGuideService } from "../../services/merchants";
import { TourGuideType } from "../../components/AdminMerchant/Retail/TourGuide/constant";
import { getCurrentDomain } from "../../services/api/retail/domainApi";
import { IDomainInfor } from "../../components/AdminMerchant/Retail/Domain";
import ModalSyncState from "../../components/Modal/Fnb/ModalSyncState";
import { useFlag, useUnleashContext } from "@unleash/proxy-client-react";
import { useLocation } from "react-router-dom";
import { BRAND_TYPE_KEIVI } from "../../services/retail/setting/package_shop_name";
import Platform from "../../pages/platform/Platform";
import { MerchantListUsingEmenu } from "../../constants/merchant-list-using-emenu";
import { FEATURE_USE_EMENU_IN_KVWEB } from "../../utils/featureFlag";

interface IAdminMerchantContext {
  retail: {
    branches?: IBranchesInfo[];
    branchInfo?: IRetailBranchInfo;
    merchantInfo?: IRetailMerchantInfo;
    seoInfo?: IRetailSeoInfo;
    settingInfo?: IRetailSettingInfo;
    fnb_version?: string;
    is_keivi_region?: boolean;
    ignore_phrases?: string[];
  };
  updateRetail: ({ key, value }: IUpdateInfo) => void;
  isLoading: boolean;
  isSync?: boolean;
  setIsSyncing?: (isSync: boolean) => void;
  processElement?: React.ReactNode;
  setProcessElement?: (processElement: React.ReactNode) => void;
  isOpenMenu: boolean;
  setIsOpenMenu: (isOpenMenuInMobile: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  fetchSyncStatus?: () => Promise<SyncStatusType>;
  initialDataMerchant?: (playform: string) => Promise<any>;
  tourGuideSteps: TourGuideType[];
  domainInfo?: IDomainInfor;
  setDomainInfo?: (data: IDomainInfor) => void;
  fetchOnboardingFeature: (platform: string) => void;
  onCheckSyncStatus?: () => void;
  isHideFeatureEmenuFromWebsiteBuilder: boolean
}

export type AdminMerchantContextProviderProps = {
  children: React.ReactNode;
};

const AdminMerchantContext = createContext<IAdminMerchantContext>(
  {} as IAdminMerchantContext
);

export function useAdminMerchantContext() {
  return useContext(AdminMerchantContext);
}

interface IUpdateInfo {
  key: "merchantInfo" | "branchInfo" | "settingInfo" | "seoInfo" | "is_keivi_region";
  value?:
    | IRetailBranchInfo
    | IRetailMerchantInfo
    | IRetailSettingInfo
    | IRetailSeoInfo
    | boolean
    | {};
}

export const AdminMerchantContextProvider: React.FC<
  AdminMerchantContextProviderProps
> = ({ children }) => {
  const location = useLocation();
  const router = useRouter();
  const dispatch = useDispatch();
  const [retail, setRetail] = useState<{
    branches?: IBranchesInfo[];
    branchInfo?: IRetailBranchInfo;
    merchantInfo?: IRetailMerchantInfo;
    settingInfo?: IRetailSettingInfo;
    fnb_version?: string;
    is_keivi_region?: boolean
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);
  const [isSync, setIsSyncing] = useState<boolean>(false);
  const [processElement, setProcessElement] = useState<React.ReactNode>(<></>);
  const shopInfo = useAppSelector(ShopInformation);
  const [guideOnboarding, setGuideOnboarding] = useState<TourGuideType[]>([]);
  const [domainInfor, setDomainInfor] = useState<IDomainInfor>({
    domainDefault: "",
    yourDomain: "",
    type: "free",
    slug: "",
  });
  const [processPercent, setProcessPercent] = useState<number>(0);
  const [openModalWarningProduct, setOpenModalWarningProduct] =
    useState<boolean>(false);
  const updateContext = useUnleashContext();
  const getDataMerchant = async (platform: string) => {
    setIsLoading(true);
    dispatch(setPageLoading({ value: true }));
    try {
      const promise = [initialDataMerchant(platform)];
      await Promise.all(promise);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
      dispatch(setPageLoading({ value: false }));
    }
  };
  const updateRetail = async ({ key, value }: IUpdateInfo) => {
    setRetail((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };
  useEffect(() => {
    if (router.query.platform) {
      getDataMerchant(router.query.platform);
    }
  }, [router.query.platform]);

  useMerchantActive(retail.merchantInfo, retail.branchInfo);

  const getShopPackage = async () => {
    if (!isEmpty(shopInfo.shop)) return;
    if (retail.merchantInfo?.ref_id) {
      try {
        const responseShopPackage = await getShopPackageSetting({
          merchant_id: retail.merchantInfo.ref_id,
          from: "admin-merchant",
          platform: router.query.platform || "retail",
          isKeivi: typeof retail?.merchantInfo?.brand_type && retail?.merchantInfo?.brand_type === BRAND_TYPE_KEIVI
        });

        dispatch(
          setIsOpenNotiEndSubscription({
            value: responseShopPackage.data.isShopEndOfSubscription,
          })
        );

        dispatch(
          setIsShopInactive({
            value: responseShopPackage.data.isShopInactive,
          })
        );
        dispatch(
          setShopInfo({
            shop: responseShopPackage.data,
            isOpenNotiEndSubscription:
              responseShopPackage.data.isShopEndOfSubscription,
            isShopInactive: responseShopPackage.data.isShopInactive,
            isShowAlertEndSubscription:
              responseShopPackage.data.isShowAlertEndSubscription,
            isShopUpgradePackageSuccess:
              responseShopPackage.data.isShopUpgradeSuccess,
          })
        );
        dispatch(
          setIsShowAlertEndSubscription({
            value: responseShopPackage.data.isShowAlertEndSubscription,
          })
        );
        dispatch(
          setIsShopUpgradePackageSucce({
            value: responseShopPackage.data.isShopUpgradePackageSuccess,
          })
        );
      } catch (error) {
        console.log({ error });
      }
    }
  };
  useEffect(() => {
    getShopPackage();
    fetchSyncStatus();
  }, [retail]);

  const fetchSyncStatus = async (): Promise<SyncStatusType | any> => {
    const platform = getPlatform();
    try {
      if (isEmpty(retail)) return;
      const res: SyncStatusType = await getSyncState(
        retail.branchInfo?.platform
      );
      if (res) {
        if (res.state !== undefined) {
          switch (res.state) {
            case "syncing":
              dispatch(setSyncStatus(true));
              dispatch(setSyncData(res));
              break;
            case "synced":
              dispatch(setSyncStatus(false));
              dispatch(setSyncData(res));
              break;
          }
        }
        return { ...res };
      }
      return {};
    } catch (error: any) {
      //console error status code 500
      if (error.response?.status === 500) {
        return {};
      } else {
        setTimeout(() => {
          fetchSyncStatus();
        }, 2000);
      }
    }
  };

  const listPathIgnore = [
    "/preview-v2/",
    "/preview-v2?",
    "/preview/",
    "/preview?",
  ];

  const initialDataMerchant = async (platform: string, fetchAll?: boolean) => {
    const matchPath = listPathIgnore.some(path => location.pathname.includes(path));
    const promise = !matchPath ? [
      getMerchantInfo({ platform }),
      getCurrentDomain(),
      // TourGuideService.index(),
      fetchOnboardingFeature(platform),
    ] : [
      getMerchantInfo({ platform }),
      // getCurrentDomain(),
      // TourGuideService.index(),
    ];
    const isKeivi = String(window.location.search.includes("is_keivi=true")) || localStorage.getItem("isKeivi")
    const [res, currentDomain] = await Promise.all(promise);
    if (
      res.branch &&
      res.merchant &&
      (platform === "fnb" ||
        (platform === "retail" && res.settings) ||
        platform === BOOKING_PLATFORM ||
        platform === BOOKING_HOTEL_PLATFORM)
    ) {
      const isShowPopupDone = localStorage.getItem(
        "is_show_popup_onboard_done"
      );
      res.branch.is_show_popup_onboard_done =
        isShowPopupDone === "true" ? true : false;
      setRetail({
        branches: res.branches,
        branchInfo: res.branch,
        merchantInfo: res.merchant,
        settingInfo: res.settings,
        fnb_version: res.fnb_version,
        is_keivi_region: isKeivi === "true" || res?.merchant?.brand_type === BRAND_TYPE_KEIVI
      });
    }

    if (!!currentDomain) {
      setDomainInfor({ ...currentDomain });
    }

    // if (tourGuideResp.status === 200 && tourGuideResp.data) {
    //   setGuideOnboarding(tourGuideResp.data.steps || []);
    // }

    await updateContext({ userId: res.merchant._id })
  };

  const fetchOnboardingFeature = async (platform: string) => {
    const responseActiveFeature = await getSettingsActiveFeatures(platform);
    if (responseActiveFeature?.success) {
      dispatch(setOnboardingSettings({ value: responseActiveFeature.data }));
    }
  };

  const handleCheckSyncStatus = async () => {
    setOpenModalWarningProduct(true);
  };

  const isHideFeatureEmenuFromWebsiteBuilder = !useFlag(FEATURE_USE_EMENU_IN_KVWEB) && (getPlatform() || retail?.merchantInfo?.platform) === FNB_PLATFORM

  return (
    <AdminMerchantContext.Provider
      value={{
        retail,
        updateRetail,
        isLoading,
        isSync,
        setIsSyncing,
        processElement,
        setProcessElement,
        isOpenMenu,
        setIsOpenMenu,
        setIsLoading,
        fetchSyncStatus,
        initialDataMerchant,
        tourGuideSteps: guideOnboarding,
        domainInfo: domainInfor,
        setDomainInfo: setDomainInfor,
        fetchOnboardingFeature,
        onCheckSyncStatus: handleCheckSyncStatus,
        isHideFeatureEmenuFromWebsiteBuilder
      }}
    >
      {children}
      <ModalSyncState
        open={openModalWarningProduct}
        setOpen={setOpenModalWarningProduct}
        processPercent={processPercent}
        setProcessPercent={setProcessPercent}
      />
    </AdminMerchantContext.Provider>
  );
};
